import { format, Locale } from 'date-fns';
import { connect, getIn, FormikContextType } from 'formik';
import React, { useEffect } from 'react';
import {
  BORDER_COLOR_SHADE_2,
  PRIMARY_BLUE,
  VERTICAL_SEPARATOR,
  WHITE,
} from '~/Constants/Colors.constants';
import { FONT_SIZE_SMALLISH } from '~/Constants/UI.constants';
import { useDateFns } from '~/Hooks';
import { Checkbox } from '~/UIKit';
import { ExtendedEventSeries } from '../../../extendedEventSeries.model';
import { IWeekDays, WeekDays } from '~/Models/interfaces/WeekDays.model';
import { makeStyles } from '~/UIKit/Style';
import { TIME_FREQUENCY } from '~/Models/DTO/EventSeries.model';
export interface EventScheduleTimeSeriesDayOfTheWeekInputProps {
  startDate: string;
}

const useStyles = makeStyles(() => ({
  dayOfTheWeekInputContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingRight: 15,
    paddingLeft: 15,
    height: 50,
    alignItems: 'center',
    borderBottom: `thin solid ${VERTICAL_SEPARATOR}`,
  },
  dayOfTheWeekInputButton: {
    width: 38,
    minWidth: 38,
    height: 38,
    fontSize: FONT_SIZE_SMALLISH,
    lineHeight: '27px',
    textAlign: 'center',
    border: `thin solid ${BORDER_COLOR_SHADE_2}`,
    borderRadius: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&.Mui-checked': {
      backgroundColor: `${PRIMARY_BLUE} !important`,
      color: WHITE,
    },
  },
}));

export const EventScheduleTimeSeriesDayOfTheWeekInput = connect<
  EventScheduleTimeSeriesDayOfTheWeekInputProps,
  ExtendedEventSeries
>(
  ({
    formik,
    startDate,
  }: EventScheduleTimeSeriesDayOfTheWeekInputProps & {
    formik: FormikContextType<ExtendedEventSeries>;
  }) => {
    function getWeekDays(locale: Locale) {
      const baseDate = new Date(Date.UTC(2017, 0, 2));
      const weekDays = [];
      for (let i = 0; i < 7; i++) {
        weekDays.push(baseDate.toLocaleDateString(locale.code, { weekday: 'long' })[0]);
        baseDate.setDate(baseDate.getDate() + 1);
      }
      return weekDays;
    }

    const { currentLocale } = useDateFns();
    const { classes } = useStyles();

    const setFieldValue = (field: keyof IWeekDays, value: boolean) => () => {
      formik.setFieldValue(field, !value);
    };

    function multipleSelectedDays() {
      const dayOptions = (({ sunday, monday, tuesday, wednesday, thursday, friday, saturday }) => ({
        sunday,
        monday,
        tuesday,
        wednesday,
        thursday,
        friday,
        saturday,
      }))(formik.values);

      const days = Object.values(dayOptions).filter((day) => day === true);

      return days.length > 1;
    }

    useEffect(() => {
      if (startDate !== formik.initialValues.startDate && !multipleSelectedDays()) {
        const weekday = format(new Date(startDate), 'eeee').toLowerCase() as keyof IWeekDays;
        const selected = WeekDays.find((day) => formik.values[day]) || weekday;
        formik.setValues((prev) => ({
          ...prev,
          [selected]: false,
          [weekday]: true,
          endDate: startDate,
        }));
      }
    }, [startDate]);

    useEffect(() => {
      setTimeout(() => {
        formik.setValues((prev) => ({
          ...prev,
          frequency: TIME_FREQUENCY.WEEKLY,
          byMonthDay: null,
          bySetPos: null,
        }));
      }, 0);
    }, []);

    return (
      <div
        className={classes.dayOfTheWeekInputContainer}
        data-testid="EventScheduleTimeSeriesDayOfTheWeekInput"
      >
        {getWeekDays(currentLocale).map((t, index) => {
          const value = getIn(formik?.values, WeekDays[index]);
          return (
            <Checkbox
              data-testid={`weekday.${WeekDays[index]}`}
              icon={<span>{t}</span>}
              checkedIcon={<span>{t}</span>}
              className={classes.dayOfTheWeekInputButton}
              value={WeekDays[index]}
              checked={value}
              onClick={setFieldValue(WeekDays[index], value)}
              key={`tsetset_${index}`}
            />
          );
        })}
      </div>
    );
  },
);
